import Layout from '@/components/Layout/Vanilla';
import Section from '@/components/Section';
import Hero from '@/components/Hero';
import { DefaultLogoReel } from '@/components/LogoReel';
import Button from '@/components/Button';
import Heading from '@/components/renderers/Heading';
import MarkdownRenderer from '@/components/MarkdownRenderer';
import CheckmarkSVG from '@/svg/icons/checkmark_outline.svg';
import { fetchAPI, gql } from '@/lib/graphcms';

const friendlyCategories = {
  Static_Site_Generators: 'Static Site Generators',
  Cloud: 'Cloud',
  Search_And_Discovery: 'Search & Discovery',
  Hosting_And_Deploying: 'Hosting & Deploying',
  Cloud_And_Infrastructure: 'Cloud & Infrastructure',
  Frontend: 'Frontend',
};

export default function EcosystemPartner({
  ecosystemPartner: { title, url, categories, ...page },
}) {
  return (
    <Layout seo={{ ...page, title }}>
      <Section>
        <Hero
          alignment="left"
          title={title}
          subTitle={`Learn more about working with ${title} on your next digital project.`}
          {...page}
          coverImage={{ ...page.coverImage, width: 300, height: 270 }}
        >
          <Button href={url} appearance="outline" colour="pink" size="large">
            Visit partner website &#8594;
          </Button>
        </Hero>
      </Section>

      <DefaultLogoReel />

      <Section>
        <div className="max-w-6xl mx-auto">
          <div className="md:flex md:flex-row-reverse -mx-6">
            <div className="w-full md:w-2/3 px-6 mb-3 md:mb-0">
              <Heading level={2} className="-mt-6">
                {title}
              </Heading>
              <MarkdownRenderer {...page} />
              <div className="-mx-3 flex">
                <div className="px-3">
                  <Button href={url} colour="pink" size="large">
                    Visit Website &#8594;
                  </Button>
                </div>
                <div className="px-3">
                  <Button
                    href="/contact"
                    appearance="minimal"
                    colour="pink"
                    size="large"
                  >
                    Contact us
                  </Button>
                </div>
              </div>
            </div>

            <div className="w-full md:w-1/3 px-6">
              <div className="bg-whisper rounded p-6 pb-4">
                <ul className="mb-6">
                  <li className="font-bold md:uppercase tracking-wide text-sm mb-6 leading-relaxed">
                    Category
                  </li>

                  {categories.map((category) => (
                    <li
                      key={category}
                      className="text-mid-gray text-sm mb-2 justify-start w-full flex items-center"
                    >
                      <CheckmarkSVG className="text-cornflower fill-current w-5 mr-3" />
                      <span>{friendlyCategories[category]}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </Section>
    </Layout>
  );
}

export const getStaticProps = async ({ preview = false, params }) => {
  const { ecosystemPartner } =
    (await fetchAPI(
      gql`
        fragment seoData on Seo {
          changefreq
          description
          image {
            url
            width
            height
            mimeType
          }
          keywords
          noindex
          priority
          title
        }

        fragment assetDataXL on Asset {
          id
          url(transformation: { image: { resize: { width: 960, fit: clip } } })
          handle
          width
          height
          mimeType
          fileName
          alt
        }

        fragment assetDataXS on Asset {
          id
          handle
          width
          height
          mimeType
          fileName
          url(transformation: { image: { resize: { width: 300, fit: clip } } })
          alt
        }

        query ecosystemPartnersContentPage($slug: String!) {
          ecosystemPartner(where: { slug: $slug }) {
            id
            title
            content
            logo {
              ...assetDataXS
            }
            coverImage {
              ...assetDataXL
            }
            url
            categories
            meta: seo {
              ...seoData
            }
            updatedAt
          }
        }
      `,
      {
        preview,
        variables: {
          slug: params.slug,
        },
      },
    )) || [];

  if (!ecosystemPartner) {
    return {
      notFound: true,
    };
  }

  return {
    props: {
      preview,
      ecosystemPartner,
    },
    revalidate: 3600,
  };
};

export const getStaticPaths = async () =>
  Promise.resolve({
    paths: [],
    fallback: `blocking`,
  });
